import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useAuthContext } from 'modules/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Copy, Trash2, Plus, Loader2, MoreHorizontal, Check } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  DataTableToolbar,
  TableSearch,
  SegmentedTabs,
  ColumnsToggle,
  TableCount,
  RefChip,
  DataTablePagination,
} from 'components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import EmptyState from 'components/ui/EmptyState';
import { useToast } from 'components/ui/use-toast';
import * as Invite from 'modules/invite';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from 'modules/auth/permissions';

type InviteRow = {
  id: string;
  code: string;
  usedAt?: string | null;
  expiresAt?: string | null;
  candidate?: { id: string; name?: string; email?: string } | null;
  createdAt?: string;
};

type CodeStatus = 'used' | 'expired' | 'unused';
const getStatus = (row: InviteRow): CodeStatus => {
  if (row.usedAt) return 'used';
  if (row.expiresAt && new Date(row.expiresAt) < new Date()) return 'expired';
  return 'unused';
};
const statusVariant = (s: CodeStatus) => (s === 'unused' ? 'success' : s === 'used' ? 'info' : 'secondary');

export const UnusedCodesPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Permission guard
  useEffect(() => {
    if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.ACCESS_KEYS_CREATE))
      navigate('/app');
  }, [authState.isFetched, authState.permissions, navigate]);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [statusTab, setStatusTab] = useState<'all' | CodeStatus>('all');
  const { page, pageSize, setPage, setPageSize } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 10,
  });
  const [cols, setCols] = useState({ status: true, usedBy: true, expires: true });

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['invites', 'test', testId],
    queryFn: async () => {
      const { data } = await Invite.Api.GetByTest(testId!);
      return data as InviteRow[];
    },
    enabled: Boolean(testId),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => Invite.Api.Delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', 'test', testId] });
      toast.success(t('code_removed'), { description: t('unused_code_has_been_deleted') });
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as any).message) : t('failed_to_delete');
      toast.error(t('error'), { description: msg });
    },
  });

  // Client-side filter (status tab + code search) then paginate.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invites.filter((row) => {
      if (statusTab !== 'all' && getStatus(row) !== statusTab) return false;
      if (q && !row.code.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [invites, search, statusTab]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Snap back to a valid page whenever the filtered set shrinks.
  useEffect(() => {
    setPage(1);
  }, [search, statusTab, pageSize, setPage]);

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      toast.success(t('copied'), { description: t('code_copied_to_clipboard') });
      setTimeout(() => setCopiedId(null), 2000);
    });
  };
  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (!testId) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <h2 className="text-xl font-bold text-foreground sm:text-2xl">{t('unused_codes')}</h2>
        <Button asChild className="w-full sm:w-auto">
          <Link to={`/app/tests/${testId}/access`}>
            <Plus className="h-4 w-4 mr-2" />
            {t('generate_codes_2')}
          </Link>
        </Button>
      </div>

      <DataTableToolbar>
        <TableSearch value={search} onChange={setSearch} placeholder={t('search_by_code')} aria-label={t('search_by_code')} />
        <ColumnsToggle
          columns={[
            { id: 'status', label: t('status'), visible: cols.status },
            { id: 'usedBy', label: t('used_by_2'), visible: cols.usedBy },
            { id: 'expires', label: t('expires'), visible: cols.expires },
          ]}
          onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
        />
        <SegmentedTabs<'all' | CodeStatus>
          aria-label={t('status')}
          value={statusTab}
          onChange={setStatusTab}
          options={[
            { value: 'all', label: t('all') },
            { value: 'unused', label: t('status_unused') },
            { value: 'used', label: t('status_used') },
            { value: 'expired', label: t('status_expired') },
          ]}
        />
        <TableCount>{filtered.length} {t('items')}</TableCount>
      </DataTableToolbar>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12">
          <EmptyState
            icon={<Tag size={28} />}
            title={t('no_invite_codes_for_this_test_yet')}
            action={{ label: t('generate_codes_2'), onClick: () => navigate(`/app/tests/${testId}/access`) }}
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('code')}</TableHead>
                  {cols.status && <TableHead>{t('status')}</TableHead>}
                  {cols.usedBy && <TableHead>{t('used_by_2')}</TableHead>}
                  {cols.expires && <TableHead>{t('expires')}</TableHead>}
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => {
                  const status = getStatus(row);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <RefChip>{row.code}</RefChip>
                      </TableCell>
                      {cols.status && (
                        <TableCell>
                          <Badge variant={statusVariant(status)}>{t(`status_${status}`)}</Badge>
                        </TableCell>
                      )}
                      {cols.usedBy && (
                        <TableCell className="text-muted-foreground">
                          {row.candidate?.name || row.candidate?.email || '—'}
                        </TableCell>
                      )}
                      {cols.expires && (
                        <TableCell className="text-muted-foreground">{formatDate(row.expiresAt)}</TableCell>
                      )}
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => copyToClipboard(row.code, row.id)}>
                              {copiedId === row.id ? (
                                <Check className="h-4 w-4 mr-2 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4 mr-2" />
                              )}
                              {t('copy_code')}
                            </DropdownMenuItem>
                            {status === 'unused' && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => deleteMutation.mutate(row.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('delete')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

      {filtered.length > 0 && (
        <DataTablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default UnusedCodesPage;
